<template>
  <div class="reviews-summary-wrap">

    <div v-if="loading" class="d-flex justify-center align-center py-4">
      <v-progress-circular indeterminate size="22" width="2" color="amber darken-2"></v-progress-circular>
    </div>

    <div class="reviews-summary" v-else-if="summary">

    <!-- Rating distribution (Eccellente / Buono / Nella media / Scarso / Terribile) -->
    <div class="distribution-block" v-if="summary.distribution && summary.distribution.length">
      <div
        v-for="(row, i) in summary.distribution"
        :key="'dist-' + i"
        class="distribution-row"
      >
        <span class="distribution-label">{{ row.label }}</span>
        <span class="distribution-track">
          <span class="distribution-fill" :style="{ width: barWidth(row.count) + '%' }"></span>
        </span>
        <span class="distribution-count">{{ row.count }}</span>
      </div>
    </div>

    <v-divider
      v-if="hasDistribution && hasCategories"
      class="my-3 fw"
    ></v-divider>

    <!-- Category scores (Servizio / Cibo / Qualità-prezzo / Atmosfera) -->
    <div class="category-block" v-if="hasCategories">
      <div
        v-for="(cat, i) in summary.categories"
        :key="'cat-' + i"
        class="category-row"
      >
        <span class="category-label">{{ cat.label }}</span>
        <span class="category-dots">
          <svg v-for="n in 5" :key="n" viewBox="0 0 16 16" class="dot-svg">
            <circle cx="8" cy="8" r="7" class="dot-bg" />
            <defs>
              <clipPath :id="clipId(i, n)">
                <rect x="0" y="0" :width="16 * dotFill(cat.score, n)" height="16" />
              </clipPath>
            </defs>
            <circle cx="8" cy="8" r="7" class="dot-fill" :clip-path="'url(#' + clipId(i, n) + ')'" />
          </svg>
        </span>
        <span class="category-score">{{ formatScore(cat.score) }}</span>
      </div>
    </div>

    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
export default {
  name: 'Reviews',
  props: {
    // 'ibook' | 'tripadvisor' | 'google'
    channel:    { type: String, required: true },
    locationId: { type: String, default: null },
    active:     { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      loaded:  false,
      // Expected shape once loaded:
      // {
      //   distribution: [ { label: 'Eccellente', count: 1906 }, ... ],
      //   categories:   [ { label: 'Servizio', score: 4.7 }, ... ]
      // }
      summary: null,
    }
  },

  computed: {
    ...mapGetters(['settings']),
    hasDistribution() {
      return !!(this.summary && this.summary.distribution && this.summary.distribution.length)
    },
    hasCategories() {
      return !!(this.summary && this.summary.categories && this.summary.categories.length)
    },
    maxCount() {
      if (!this.hasDistribution) return 1
      return Math.max(1, ...this.summary.distribution.map(d => Number(d.count) || 0))
    },
  },

  methods: {
    barWidth(count) {
      const c = Number(count) || 0
      return Math.max(0, Math.min(100, (c / this.maxCount) * 100))
    },

    // Fraction (0..1) of the nth dot (1-indexed) that should be filled for a given score
    dotFill(score, n) {
      const remaining = (Number(score) || 0) - (n - 1)
      if (remaining >= 1) return 1
      if (remaining <= 0) return 0
      return remaining
    },

    formatScore(score) {
      const n = Number(score)
      return Number.isFinite(n) ? n.toFixed(1) : score
    },

    clipId(i, n) {
      return 'reviews-summary-dot-clip-' + this._uid + '-' + i + '-' + n
    },

    async fetchSummary() {
      if (this.loaded || this.loading) return
      this.loading = true
      try {
        const payload = {
          action: 'Reviews_Summary_Get',
          channel: this.channel,
        }
        if (this.channel === 'tripadvisor') {
          payload.locationId = this.settings?.options?.tripadvisorId || null
        } else if (this.channel === 'google') {
          payload.locationId = this.settings?.options?.googleReviewId || null
        } else {
          // 'ibook' — our own reviews, identified by this restaurant's customerId
          payload.customerId = this.customerId
        }

        const res = await this.$http.post('/review/review.php', payload)
        if (res.data.errorid === '0') {
          this.summary = res.data.payloads || null
        }
        this.loaded = true
      } catch (e) {
        console.error('ReviewsSummary fetchSummary error', e)
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    active(val) {
      if (val) this.fetchSummary()
    },
    channel() {
      // A different channel needs its own summary — reset and refetch if visible
      this.loaded = false
      this.summary = null
      if (this.active) this.fetchSummary()
    },
    settings(val) {
      // settings arrived after we already (unsuccessfully) fetched without an id — retry once
      if (val && this.active && !this.summary) {
        this.loaded = false
        this.fetchSummary()
      }
    },
  },

  created() {
    if (this.active) this.fetchSummary()
  },
}
</script>

<style scoped>
.reviews-summary {
  padding: 14px 16px 4px;
}
 
.distribution-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
 
.distribution-row:last-child {
  margin-bottom: 0;
}
 
.distribution-label {
  flex: 0 0 92px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #37474F;
}
 
.distribution-track {
  flex: 1 1 auto;
  height: 10px;
  border-radius: 5px;
  background: #e8e8e8;
  overflow: hidden;
  position: relative;
}
 
.distribution-fill {
  display: block;
  height: 100%;
  border-radius: 5px;
  background: #ffc107; /* amber */
  transition: width 0.3s ease;
}
 
.distribution-count {
  flex: 0 0 40px;
  text-align: right;
  font-size: 0.78rem;
  color: #757575;
}
 
.category-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
 
.category-row:last-child {
  margin-bottom: 0;
}
 
.category-label {
  flex: 0 0 92px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #37474F;
}
 
.category-dots {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: space-between
}
 
.dot-svg {
  width: 15px;
  height: 15px;
}
 
.dot-bg {
  fill: #e8e8e8;
}
 
.dot-fill {
  fill: #ffc107; /* amber darken-2 */
}
 
.category-score {
  flex: 0 0 40px;
  text-align: right;
  font-size: 0.78rem;
  font-weight: 700;
  color: #37474F;
}
</style>